import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContractWiserLogo } from "@/components/ContractWiserLogo";

export default function PrivacyPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Privacy Policy | ContractWiser";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <ContractWiserLogo size="default" />
          </button>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-eucalypt-700">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-eucalypt-800 mb-8">
              Privacy Policy
            </h1>
            
            <div className="prose prose-eucalypt max-w-none space-y-6 text-muted-foreground">
              <p className="text-sm">Last updated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">1. Introduction</h2>
                <p>ContractWiser ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and handle your information when you use our property contract analysis service ("the Service").</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">2. Information We Collect</h2>
                <p>When you use the Service, we collect:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li><strong>Email address:</strong> To send you your analysis report</li>
                  <li><strong>Property state and type:</strong> To tailor the analysis to your jurisdiction</li>
                  <li><strong>Uploaded contract documents:</strong> To perform the analysis</li>
                  <li><strong>Optional concerns:</strong> Any specific concerns you share about your contract</li>
                  <li><strong>Payment information:</strong> Processed securely by Stripe (we do not store card details)</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">3. How We Use Your Information</h2>
                <p>We use your information solely to:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>Generate your contract analysis report</li>
                  <li>Send the report to your email address</li>
                  <li>Process your payment</li>
                  <li>Respond to your inquiries or support requests</li>
                  <li>Improve our Service (using anonymized, aggregated data only)</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">4. Document Handling and Deletion</h2>
                <p className="font-semibold text-eucalypt-800">Your uploaded contract documents are automatically deleted immediately after your analysis is generated.</p>
                <p className="mt-4">We do not:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>Store your contract documents after analysis</li>
                  <li>Share your documents with third parties</li>
                  <li>Use your documents for training AI models</li>
                  <li>Retain copies of your documents for any purpose</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">5. Third-Party Services</h2>
                <p>We use the following third-party services:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li><strong>Stripe:</strong> For secure payment processing. Stripe's privacy policy applies to payment data.</li>
                  <li><strong>AI Services:</strong> To analyze documents. Document content is processed but not stored by these services.</li>
                  <li><strong>Email Services:</strong> To deliver your report via email.</li>
                </ul>
                <p className="mt-4">We do not sell, trade, or rent your personal information to third parties.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">6. Data Retention</h2>
                <p>We retain the following information:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li><strong>Email address and submission metadata:</strong> Retained for transaction records and customer support purposes</li>
                  <li><strong>Generated reports:</strong> Retained to allow you to access your report</li>
                  <li><strong>Uploaded documents:</strong> Deleted immediately after analysis</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">7. Data Security</h2>
                <p>We implement reasonable security measures to protect your information, including:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>Encrypted data transmission (HTTPS/TLS)</li>
                  <li>Secure cloud infrastructure</li>
                  <li>Limited access to personal information</li>
                </ul>
                <p className="mt-4">However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your data.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">8. Your Rights</h2>
                <p>Under Australian Privacy Principles, you have the right to:</p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information (subject to legal retention requirements)</li>
                  <li>Opt out of marketing communications</li>
                </ul>
                <p className="mt-4">To exercise these rights, contact us at <a href="mailto:admin@contractwiser.com.au" className="text-eucalypt-700 hover:underline">admin@contractwiser.com.au</a></p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">9. Cookies</h2>
                <p>We use essential cookies only to ensure the Service functions properly. We do not use tracking cookies or third-party advertising cookies.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">10. Children's Privacy</h2>
                <p>The Service is not intended for individuals under 18 years of age. We do not knowingly collect information from children.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">11. International Data Transfers</h2>
                <p>Your information may be processed on servers located outside Australia. By using the Service, you consent to this transfer. We ensure appropriate safeguards are in place for international data transfers.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">12. Changes to This Policy</h2>
                <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on our website. Continued use of the Service after changes constitutes acceptance.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl text-eucalypt-800 mt-8 mb-4">13. Contact Us</h2>
                <p>For privacy-related inquiries or concerns, contact us at:</p>
                <p className="mt-2">
                  <strong>Email:</strong> <a href="mailto:admin@contractwiser.com.au" className="text-eucalypt-700 hover:underline">admin@contractwiser.com.au</a>
                </p>
              </section>

              <div className="mt-12 p-6 bg-eucalypt-50 border-l-4 border-eucalypt-500">
                <p className="font-semibold text-eucalypt-800">Your Privacy Matters</p>
                <p className="mt-2">We are committed to handling your data responsibly. Your uploaded documents are deleted immediately after analysis - we do not keep copies of your contracts.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
